import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Play, CalendarCheck2, MessageCircle, Clock3, ListChecks, FileText } from "lucide-react";
import { MaskLine, Reveal } from "./Reveal";
import { scrollToHash } from "./SmoothScroll";

const MOCK_ROWS = [
  { icon: CalendarCheck2, label: "Scadenza · Adempimento mensile", status: "In lavorazione", tone: "text-amber-300 bg-amber-400/10 border-amber-400/20" },
  { icon: FileText, label: "Documento · Fattura", status: "Disponibile", tone: "text-cyan-300 bg-cyan-400/10 border-cyan-400/20" },
  { icon: ListChecks, label: "Adempimento · Dichiarazione", status: "Completato", tone: "text-emerald-300 bg-emerald-400/10 border-emerald-400/20" },
];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yCard = useTransform(scrollYProgress, [0, 1], [0, -70]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const opacity = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section
      ref={ref}
      id="home"
      data-testid="hero-section"
      className="relative flex min-h-screen items-center overflow-hidden pb-20 pt-32 lg:pt-24"
    >
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-40 left-[8%] h-[560px] w-[560px] rounded-full bg-[#1B6EF3]/25 blur-[140px]" />
        <div className="absolute right-[-8%] top-[20%] h-[480px] w-[480px] rounded-full bg-[#19D6E8]/18 blur-[130px]" />
        <div className="absolute bottom-[-20%] left-[35%] h-[420px] w-[420px] rounded-full bg-[#38BDF8]/12 blur-[120px]" />
        <div className="noise absolute inset-0 opacity-60" />
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.035)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.035)_1px,transparent_1px)] bg-[size:72px_72px] [mask-image:radial-gradient(ellipse_70%_60%_at_50%_35%,black,transparent)]" />
      </div>

      <div className="relative mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-14 px-5 sm:px-8 lg:grid-cols-[1.2fr_0.8fr] lg:gap-12">
        <motion.div style={{ y: yText, opacity }} className="flex flex-col gap-7">
          <div className="inline-flex items-center gap-2.5 self-start rounded-full border border-white/15 bg-white/5 px-4 py-2 backdrop-blur">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-cyan-400 opacity-60" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-cyan-300" />
            </span>
            <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-slate-300">
              Tecnologia all'avanguardia · Professionisti interni
            </span>
          </div>

          <h1 className="font-display text-4xl font-extrabold leading-[1.12] tracking-tight sm:text-5xl lg:text-[2.5rem] lg:leading-[1.18] xl:text-[2.9rem]">
            <MaskLine delay={0.1}>Più tempo al tuo futuro.</MaskLine>
            <MaskLine delay={0.26} className="text-gradient">Alla tua P.IVA ci pensiamo noi.</MaskLine>
          </h1>

          <MaskLine delay={0.55} className="max-w-xl">
            <span className="block text-base leading-relaxed text-slate-400 sm:text-lg">
              Puoi pensare al tuo lavoro: Human Factor semplifica la gestione della tua Partita IVA forfettaria,
              riunendo scadenze, adempimenti, documenti e supporto professionale in un unico spazio digitale.
              <span className="mt-4 block">
                Tutto è più chiaro, ordinato e facile da gestire. E quando hai bisogno di aiuto, puoi parlare
                direttamente con un professionista che conosce già la tua situazione alla perfezione.
              </span>
            </span>
          </MaskLine>

          <div className="flex flex-col gap-3.5 sm:flex-row sm:items-center">
            <motion.a
              href="#contatti"
              data-testid="hero-btn-parla-con-noi"
              onClick={(e) => {
                e.preventDefault();
                scrollToHash("#contatti");
              }}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              className="group inline-flex items-center justify-center gap-2.5 rounded-full bg-gradient-to-r from-[#1B6EF3] to-[#19D6E8] px-7 py-4 font-display text-base font-bold text-white shadow-xl shadow-blue-500/30 transition-shadow hover:shadow-cyan-400/40"
            >
              Parla ora con un professionista
              <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
            </motion.a>
            <motion.a
              href="#come-funziona"
              data-testid="hero-btn-come-funziona"
              onClick={(e) => {
                e.preventDefault();
                scrollToHash("#come-funziona");
              }}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              className="inline-flex items-center justify-center gap-2.5 rounded-full border border-white/20 bg-white/5 px-7 py-4 font-display text-base font-semibold text-white backdrop-blur transition-colors hover:border-cyan-300/50"
            >
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-white/10">
                <Play className="h-3 w-3 fill-white" />
              </span>
              Scopri come funziona
            </motion.a>
          </div>
        </motion.div>

        <motion.div style={{ y: yCard }} className="relative">
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div
              data-testid="hero-app-preview"
              className="glass relative overflow-hidden rounded-3xl p-6 shadow-2xl shadow-black/40 sm:p-7"
            >
              <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_80%_0%,rgba(25,214,232,0.15),transparent_55%)]" />
              <div className="relative mb-6 flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-[#1B6EF3] to-[#19D6E8]">
                    <img src="/hf-icon.webp" alt="" className="h-5 w-5" />
                  </span>
                  <div>
                    <p className="font-display text-sm font-bold text-white">Il tuo spazio</p>
                    <p className="font-mono text-[10px] text-slate-400">Partita IVA forfettaria</p>
                  </div>
                </div>
                <span className="flex items-center gap-1.5 rounded-full border border-emerald-400/20 bg-emerald-400/10 px-2.5 py-1 text-[10px] font-medium text-emerald-300">
                  <Clock3 className="h-3 w-3" /> Sempre aggiornato
                </span>
              </div>

              <div className="relative flex flex-col gap-3">
                {MOCK_ROWS.map((r, i) => (
                  <motion.div
                    key={r.label}
                    initial={{ opacity: 0, x: 24 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.9 + i * 0.15, duration: 0.5 }}
                    className="flex items-center justify-between gap-3 rounded-2xl border border-white/10 bg-black/20 px-4 py-3.5"
                  >
                    <span className="flex items-center gap-3">
                      <span className="flex h-8 w-8 items-center justify-center rounded-lg border border-white/10 bg-white/5 text-cyan-300">
                        <r.icon className="h-4 w-4" />
                      </span>
                      <span className="text-xs font-medium text-slate-200 sm:text-sm">{r.label}</span>
                    </span>
                    <span className={`shrink-0 rounded-full border px-2.5 py-1 text-[10px] font-semibold ${r.tone}`}>
                      {r.status}
                    </span>
                  </motion.div>
                ))}

                <motion.div
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.45, duration: 0.5 }}
                  className="mt-1 flex items-start gap-3 rounded-2xl rounded-tl-sm border border-white/10 bg-gradient-to-br from-[#1B6EF3]/20 to-[#19D6E8]/10 px-4 py-3.5"
                >
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#1B6EF3] to-[#19D6E8] font-display text-xs font-bold text-white">
                    P
                  </span>
                  <div>
                    <p className="text-xs font-semibold text-white">Il tuo professionista</p>
                    <p className="mt-0.5 text-xs leading-relaxed text-slate-300">
                      Abbiamo completato l'adempimento: trovi il documento nel tuo spazio.
                    </p>
                  </div>
                </motion.div>
              </div>
            </div>

            <motion.div
              style={{ y: useTransform(scrollYProgress, [0, 1], [0, -30]) }}
              className="animate-float absolute -right-3 -top-6 hidden items-center gap-2.5 rounded-2xl border border-white/10 bg-slate-900/90 px-4 py-3 shadow-2xl shadow-black/40 backdrop-blur-xl sm:flex"
            >
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-cyan-400/15 text-cyan-300">
                <CalendarCheck2 className="h-4 w-4" />
              </span>
              <div>
                <p className="text-xs font-semibold text-white">Scadenze più chiare</p>
                <p className="font-mono text-[10px] text-slate-400">Sai sempre che cosa e quando</p>
              </div>
            </motion.div>

            <motion.div
              style={{ y: useTransform(scrollYProgress, [0, 1], [0, 26]) }}
              className="animate-float absolute -bottom-7 -left-4 hidden items-center gap-2.5 rounded-2xl border border-white/10 bg-slate-900/90 px-4 py-3 shadow-2xl shadow-black/40 backdrop-blur-xl sm:flex"
            >
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-400/15 text-emerald-300">
                <MessageCircle className="h-4 w-4" />
              </span>
              <div>
                <p className="text-xs font-semibold text-white">Canale diretto</p>
                <p className="font-mono text-[10px] text-slate-400">Con il professionista che ti segue</p>
              </div>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>

      <motion.button
        data-testid="hero-scroll-indicator"
        onClick={() => scrollToHash("#perche-human-factor")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 0.8 }}
        className="absolute bottom-6 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-1.5 text-slate-500 transition-colors hover:text-cyan-300 md:flex"
        aria-label="Scorri verso il basso"
      >
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}>
          <ArrowRight className="h-4 w-4 rotate-90" />
        </motion.span>
      </motion.button>
    </section>
  );
}
